import React, { useEffect, useState, useMemo } from "react";
import { Typography, Button, Box } from "@mui/material";
import FormField from "../FormComponent/FormField/FormField";
import styles from './practitioner.module.css'
import { useForm } from 'react-hook-form';
import RemoveCircleOutlineOutlinedIcon from '@mui/icons-material/RemoveCircleOutlineOutlined';
import { axiosProxyInstance } from "../../utils/request";
import cogoToast from "cogo-toast";
import InfoOutlineIcon from '@mui/icons-material/InfoOutlined';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

const defaultValidationSchema = yup.object({
  initialAssessmentDuration: yup.number().nullable().positive('Duration must be a positive number').required('Initial assessment duration is required'),
  initialAssessmentPrice: yup.number().nullable().positive('Price must be a positive number').required('Initial assessment price is required'),
  followUpTreatmentDuration: yup.number().nullable().required('FollowUp treatment duration is required'),
  followUpTreatmentPrice: yup.number().nullable().required('FollowUp treatment price is required'),
});

const generateValidationSchema = (values) => {
  const schemaShape = {
    initialAssessmentDuration: yup
      .number()
      .nullable()
      .positive('Duration must be a positive number')
      .required('Initial assessment duration is required'),

    initialAssessmentPrice: yup
      .number()
      .nullable()
      .positive('Price must be a positive number')
      .required('Initial assessment price is required'),

    followUpTreatmentDuration: yup
      .number()
      .nullable()
      .positive('Duration must be a positive number')
      .required("FollowUp treatment duration is required"),

    followUpTreatmentPrice: yup
      .number()
      .nullable()
      .positive('Price must be a positive number')
      .required("FollowUp treatment price is required"),
  };

  Object.keys(values).forEach((key) => {
    // Dynamic assessment fields
    if (key.startsWith("initialAssessmentDuration")) {
      schemaShape[key] = yup
        .number()
        .nullable()
        .positive("Duration must be a positive number")
        .required("Initial assessment duration is required");
    }

    if (key.startsWith("initialAssessmentPrice")) {
      schemaShape[key] = yup
        .number()
        .nullable()
        .positive("Price must be a positive number")
        .required("Initial assessment price is required");
    }

    // Dynamic assessment fields
    if (key.startsWith("followUpTreatmentDuration")) {
      schemaShape[key] = yup
        .number()
        .nullable()
        .positive("Duration must be a positive number")
        .required("FollowUp treatment duration is required");
    }

    if (key.startsWith("followUpTreatmentPrice")) {
      schemaShape[key] = yup
        .number()
        .nullable()
        .positive("Price must be a positive number")
        .required("FollowUp treatment price is required");
    }
  });

  return yup.object().shape(schemaShape);
};

const Appointment = ({ sectionData, details }) => {
    
    const [assessmentFields, setAssessmentFields] = useState([]);
    const [treatmentFields, setTreatmentFields] = useState([]);
    const [assessmentTypeFields, setAssessmentTypeFields] = useState([]);
    const [followUpTreatmentTypes, setFollowUpTreatmentTypes] = useState([]);
    const currentUserId = localStorage.getItem("LPCurrentUserId");
    const [validationSchema, setValidationSchema] = useState(() => defaultValidationSchema);

    const resolver = useMemo(() => yupResolver(validationSchema), [validationSchema]);
    const form = useForm({
        resolver,
        mode: "onBlur",
    });
    const { register, handleSubmit, control, formState: { errors }, getValues, setValue, watch, reset } = form;
    const flattenFields = (fields = []) => {
        return fields.flatMap(field => {
            if (field.fields) {
                return flattenFields(field.fields);
            }
            return field;
        });
    };

    const allFields = flattenFields(sectionData.fields);

    const initialAssessmentDurationField = allFields.find(field => field.id === "initialAssessmentDuration");
    const initialAssessmentPriceField = allFields.find(field => field.id === "initialAssessmentPrice");
    const assessmentTypeField = allFields.find(field => field.id === "assessmentType");
    const addInitialAssessmentTypeLinkField = allFields.find(field => field.id === "addInitialAssessmentTypeLink");
    const followUpTreatmentDurationField = allFields.find(field => field.id === "followUpTreatmentDuration");
    const followUpTreatmentPriceField = allFields.find(field => field.id === "followUpTreatmentPrice");
    const followUpTreatmentTypeField = allFields.find(field => field.id === "followUpTreatmentType");
    const addFollowUpTreatmentTypeLinkField = allFields.find(field => field.id === "addFollowUpTreatmentTypeLink");

    useEffect(() => {
        if (details) {
            const assessmentArray = Object.values(details?.data?.initial_assessment || []);
            const treatmentArray = Object.values(details?.data?.follow_up_treatment || []);
            const assessmentTypeArray = Object.values(details?.data?.assessment_type || []);
            const followUpTypeArray = details?.data?.followup_treatment_type || [];
            
            let resetValueObject = {};
            if(assessmentArray?.length && treatmentArray?.length){
                const dynamicAssessmentFields = assessmentArray.map((assessment, assessmentIndex) => {
                    let assessmentFieldsData = {
                        id:"addAssessment",
                        fields: []
                    };
                    Object.entries(assessment).forEach(([key, value]) => {
                        // Remove the 'Initial_' prefix and convert values when needed
                        const newKey = key.replace('Initial_', '').toLowerCase(); // Remove the "Initial_" prefix and lowercase the key
                        if(newKey.includes('duration')){
                            if(assessmentIndex === 0){
                                const fieldId = `${initialAssessmentDurationField.id}`;
                                resetValueObject[fieldId] = value || null;
                                const fieldObj = {
                                    ...initialAssessmentDurationField,
                                    isDynamic: true,
                                    id: fieldId,
                                    defaultValue: value || null,
                                }
                                assessmentFieldsData.fields.push(fieldObj)
                            }else{
                                const fieldId = `${initialAssessmentDurationField.id}-${assessmentIndex + 1}`;
                                resetValueObject[fieldId] = value || null;
                                const fieldObj = {
                                    ...initialAssessmentDurationField,
                                    isDynamic: true,
                                    id: fieldId,
                                    deleteId: fieldId,
                                    defaultValue: value || null,
                                }
                                assessmentFieldsData.fields.push(fieldObj)
                            }
                        }else if(newKey.includes('cost')){
                            if(assessmentIndex === 0){
                                const fieldId = `${initialAssessmentPriceField.id}`;
                                resetValueObject[fieldId] = value || null;
                                const fieldObj = {
                                    ...initialAssessmentPriceField,
                                    isDynamic: true,
                                    id: fieldId,
                                    defaultValue: value || null,
                                }
                                assessmentFieldsData.fields.push(fieldObj)
                            }else{
                                const fieldId = `${initialAssessmentPriceField.id}-${assessmentIndex + 1}`;
                                resetValueObject[fieldId] = value || null;
                                const fieldObj = {
                                    ...initialAssessmentPriceField,
                                    isDynamic: true,
                                    id: fieldId,
                                    deleteId: fieldId,
                                    defaultValue: value || null,
                                }
                                assessmentFieldsData.fields.push(fieldObj)
                            }
                        }else{
                            if(assessmentIndex === 0){
                                const fieldId = `${assessmentTypeField.id}`;
                                resetValueObject[fieldId] = value;
                                const fieldObj = {
                                    ...assessmentTypeField,
                                    isDynamic: true,
                                    id: fieldId,
                                    defaultValue: value,
                                }
                                assessmentFieldsData.fields.push(fieldObj)
                            }else{
                                const fieldId = `${assessmentTypeField.id}-${assessmentIndex + 1}`;
                                resetValueObject[fieldId] = value;
                                const fieldObj = {
                                    ...assessmentTypeField,
                                    isDynamic: true,
                                    id: fieldId,
                                    deleteId: fieldId,
                                    defaultValue: value,
                                }
                                assessmentFieldsData.fields.push(fieldObj)
                            }
                        }
                    });

                    return assessmentFieldsData;
                });
                const dynamicTreatmentFields = treatmentArray.map((treatment, treatmentIndex) => {
                   let treatmentFieldsData = {
                        id:"addTreatment",
                        fields: []
                    };
                    Object.entries(treatment).forEach(([key, value]) => {
                        // Remove the 'Initial_' prefix and convert values when needed
                        const newKey = key.replace('follow_up_treatment_', '').toLowerCase(); // Remove the "Initial_" prefix and lowercase the key
                        if(newKey.includes('duration')){
                            if(treatmentIndex === 0){
                                const fieldId = `${followUpTreatmentDurationField.id}`;
                                resetValueObject[fieldId] = value || null;
                                const fieldObj = {
                                    ...followUpTreatmentDurationField,
                                    isDynamic: true,
                                    id: fieldId,
                                    defaultValue: value || null,
                                }
                                treatmentFieldsData.fields.push(fieldObj)
                            }else{
                                const fieldId = `${followUpTreatmentDurationField.id}-${treatmentIndex + 1}`;
                                resetValueObject[fieldId] = value || null;
                                const fieldObj = {
                                    ...followUpTreatmentDurationField,
                                    isDynamic: true,
                                    id: fieldId,
                                    deleteId: fieldId,
                                    defaultValue: value || null,
                                }
                                treatmentFieldsData.fields.push(fieldObj)
                            }
                        }else if(newKey.includes('cost')){
                            if(treatmentIndex === 0){
                                const fieldId = `${followUpTreatmentPriceField.id}`;
                                resetValueObject[fieldId] = value || null;
                                const fieldObj = {
                                    ...followUpTreatmentPriceField,
                                    isDynamic: true,
                                    id: fieldId,
                                    defaultValue: value || null,
                                }
                                treatmentFieldsData.fields.push(fieldObj)
                            }else{
                                const fieldId = `${followUpTreatmentPriceField.id}-${treatmentIndex + 1}`;
                                resetValueObject[fieldId] = value || null;
                                const fieldObj = {
                                    ...followUpTreatmentPriceField,
                                    isDynamic: true,
                                    id: fieldId,
                                    deleteId: fieldId,
                                    defaultValue: value || null,
                                }
                                treatmentFieldsData.fields.push(fieldObj)
                            }
                        }else{
                            if(treatmentIndex === 0){
                                const fieldId = `${followUpTreatmentTypeField.id}`;
                                resetValueObject[fieldId] = value;
                                const fieldObj = {
                                    ...followUpTreatmentTypeField,
                                    isDynamic: true,
                                    id: fieldId,
                                    defaultValue: value,
                                }
                                treatmentFieldsData.fields.push(fieldObj)
                            }else{
                                const fieldId = `${followUpTreatmentTypeField.id}-${treatmentIndex + 1}`;
                                resetValueObject[fieldId] = value;
                                const fieldObj = {
                                    ...followUpTreatmentTypeField,
                                    isDynamic: true,
                                    id: fieldId,
                                    deleteId: fieldId,
                                    defaultValue: value,
                                }
                                treatmentFieldsData.fields.push(fieldObj)
                            }
                        }
                    });

                    return treatmentFieldsData;
                });
                setAssessmentFields(dynamicAssessmentFields);
                setTreatmentFields(dynamicTreatmentFields);
            }else{
                if(assessmentTypeArray?.length){
                    const dynamicAssessmentFields = assessmentTypeArray.map((assessment, assessmentIndex) => {
                        let assessmentFieldsData = {
                            id:"addAssessment",
                            fields: []
                        };
                        if(assessmentIndex === 0){
                            const durationFieldId = `${initialAssessmentDurationField.id}`;
                            const priceFieldId = `${initialAssessmentPriceField.id}`;
                            const typeFieldId = `${assessmentTypeField.id}`;
                            resetValueObject[durationFieldId] = details?.data?.initial_appointment_time || null;
                            resetValueObject[priceFieldId] = details?.data?.initial_appointment_cost || null;
                            resetValueObject[typeFieldId] = assessment;
                            const fieldObj = {
                                ...initialAssessmentDurationField,
                                isDynamic: true,
                                id: durationFieldId,
                                defaultValue: details?.data?.initial_appointment_time || null,
                            }
                            const fieldObjPrice = {
                                ...initialAssessmentPriceField,
                                isDynamic: true,
                                id: priceFieldId,
                                defaultValue: details?.data?.initial_appointment_cost || null,
                            }
                            const fieldObjType = {
                                ...assessmentTypeField,
                                isDynamic: true,
                                id: typeFieldId,
                                defaultValue: assessment,
                            }
                            assessmentFieldsData.fields.push(fieldObj)
                            assessmentFieldsData.fields.push(fieldObjPrice)
                            assessmentFieldsData.fields.push(fieldObjType)
                        }else{
                            const durationFieldId = `${initialAssessmentDurationField.id}-${assessmentIndex + 1}`;
                            const priceFieldId = `${initialAssessmentPriceField.id}-${assessmentIndex + 1}`;
                            const typeFieldId = `${assessmentTypeField.id}-${assessmentIndex + 1}`;
                            resetValueObject[durationFieldId] = null;
                            resetValueObject[priceFieldId] = null;
                            resetValueObject[typeFieldId] = assessment;
                            const fieldObj = {
                                ...initialAssessmentDurationField,
                                isDynamic: true,
                                id: durationFieldId,
                                deleteId: durationFieldId,
                                defaultValue: null,
                            }
                            const fieldObjPrice = {
                                ...initialAssessmentPriceField,
                                isDynamic: true,
                                id: priceFieldId,
                                deleteId: priceFieldId,
                                defaultValue: null,
                            }
                            const fieldObjType = {
                                ...assessmentTypeField,
                                isDynamic: true,
                                id: typeFieldId,
                                deleteId: typeFieldId,
                                defaultValue: assessment,
                            }
                            assessmentFieldsData.fields.push(fieldObj)
                            assessmentFieldsData.fields.push(fieldObjPrice)
                            assessmentFieldsData.fields.push(fieldObjType)
                        }
                        return assessmentFieldsData;
                    });
                    setAssessmentFields(dynamicAssessmentFields);
                }else{
                    let dynamicAssessmentFields = [
                        {
                            id:"addAssessment",
                            fields: []
                        }
                    ];
                    const durationFieldId = `${initialAssessmentDurationField.id}`;
                    const priceFieldId = `${initialAssessmentPriceField.id}`;
                    const typeFieldId = `${assessmentTypeField.id}`;
                    resetValueObject[durationFieldId] = null;
                    resetValueObject[priceFieldId] = null;
                    resetValueObject[typeFieldId] = "";
                    const fieldObj = {
                        ...initialAssessmentDurationField,
                        isDynamic: true,
                        id: durationFieldId,
                        defaultValue: null,
                    }
                    const fieldObjPrice = {
                        ...initialAssessmentPriceField,
                        isDynamic: true,
                        id: priceFieldId,
                        defaultValue: null,
                    }
                    const fieldObjType = {
                        ...assessmentTypeField,
                        isDynamic: true,
                        id: typeFieldId,
                        defaultValue: "",
                    }
                    dynamicAssessmentFields[0].fields.push(fieldObj);
                    dynamicAssessmentFields[0].fields.push(fieldObjPrice);
                    dynamicAssessmentFields[0].fields.push(fieldObjType);
                    setAssessmentFields(dynamicAssessmentFields);
                }
                if(followUpTypeArray?.length){
                    const dynamicTreatmentFields = followUpTypeArray.map((treatment, treatmentIndex) => {
                        let assessmentFieldsData = {
                            id:"addTreatment",
                            fields: []
                        };
                        if(treatmentIndex === 0){
                            const durationFieldId = `${followUpTreatmentDurationField.id}`;
                            const priceFieldId = `${followUpTreatmentPriceField.id}`;
                            const typeFieldId = `${followUpTreatmentTypeField.id}`;
                            resetValueObject[durationFieldId] = details?.data?.followup_appointment_time || null;
                            resetValueObject[priceFieldId] = details?.data?.followup_appointment_cost || null;
                            resetValueObject[typeFieldId] = treatment;
                            const fieldObj = {
                                ...followUpTreatmentDurationField,
                                isDynamic: true,
                                id: durationFieldId,
                                defaultValue: details?.data?.followup_appointment_time || null,
                            }
                            const fieldObjPrice = {
                                ...followUpTreatmentPriceField,
                                isDynamic: true,
                                id: priceFieldId,
                                defaultValue: details?.data?.followup_appointment_cost || null,
                            }
                            const fieldObjType = {
                                ...followUpTreatmentTypeField,
                                isDynamic: true,
                                id: typeFieldId,
                                defaultValue: treatment,
                            }
                            assessmentFieldsData.fields.push(fieldObj)
                            assessmentFieldsData.fields.push(fieldObjPrice)
                            assessmentFieldsData.fields.push(fieldObjType)
                        }else{
                            const durationFieldId = `${followUpTreatmentDurationField.id}-${treatmentIndex + 1}`;
                            const priceFieldId = `${followUpTreatmentPriceField.id}-${treatmentIndex + 1}`;
                            const typeFieldId = `${followUpTreatmentTypeField.id}-${treatmentIndex + 1}`;
                            resetValueObject[durationFieldId] = null;
                            resetValueObject[priceFieldId] = null;
                            resetValueObject[typeFieldId] = treatment;
                            const fieldObj = {
                                ...followUpTreatmentDurationField,
                                isDynamic: true,
                                id: durationFieldId,
                                deleteId: durationFieldId,
                                defaultValue: null,
                            }
                            const fieldObjPrice = {
                                ...followUpTreatmentPriceField,
                                isDynamic: true,
                                id: priceFieldId,
                                deleteId: priceFieldId,
                                defaultValue: null,
                            }
                            const fieldObjType = {
                                ...followUpTreatmentTypeField,
                                isDynamic: true,
                                id: typeFieldId,
                                deleteId: typeFieldId,
                                defaultValue: treatment,
                            }
                            assessmentFieldsData.fields.push(fieldObj)
                            assessmentFieldsData.fields.push(fieldObjPrice)
                            assessmentFieldsData.fields.push(fieldObjType)
                        }
                        return assessmentFieldsData;
                    });
                    setTreatmentFields(dynamicTreatmentFields);
                }else{
                    let dynamicTreatmentFields = [
                        {
                            id:"addTreatment",
                            fields: []
                        }
                    ];
                    const durationFieldId = `${followUpTreatmentDurationField.id}`;
                    const priceFieldId = `${followUpTreatmentPriceField.id}`;
                    const typeFieldId = `${followUpTreatmentTypeField.id}`;
                    resetValueObject[durationFieldId] = null;
                    resetValueObject[priceFieldId] = null;
                    resetValueObject[typeFieldId] = "";
                    const fieldObj = {
                        ...initialAssessmentDurationField,
                        isDynamic: true,
                        id: durationFieldId,
                        defaultValue: null,
                    }
                    const fieldObjPrice = {
                        ...initialAssessmentPriceField,
                        isDynamic: true,
                        id: priceFieldId,
                        defaultValue: null,
                    }
                    const fieldObjType = {
                        ...assessmentTypeField,
                        isDynamic: true,
                        id: typeFieldId,
                        defaultValue: "",
                    }
                    dynamicTreatmentFields[0].fields.push(fieldObj);
                    dynamicTreatmentFields[0].fields.push(fieldObjPrice);
                    dynamicTreatmentFields[0].fields.push(fieldObjType);
                    setTreatmentFields(dynamicTreatmentFields);
                }
            }
            const newSchema = generateValidationSchema(resetValueObject);
            setValidationSchema(newSchema); // This will re-trigger useForm with new resolver
            reset(resetValueObject);

        }
    }, [details, reset]);


    const handleDetailsLinkButtonClick = (field) => {
        if (field.action === "addInitialAssessmentType") {
            let dynamicAssessmentField = {
                id:"addAssessment",
                fields: []
            };
            const dynamicFieldsCount = assessmentFields.length;
            const durationFieldId = `${initialAssessmentDurationField.id}-${dynamicFieldsCount + 1}`;
            const priceFieldId = `${initialAssessmentPriceField.id}-${dynamicFieldsCount + 1}`;
            const typeFieldId = `${assessmentTypeField.id}-${dynamicFieldsCount + 1}`;
            
            const fieldObj = {
                ...initialAssessmentDurationField,
                isDynamic: true,
                id: durationFieldId,
                defaultValue: null,
            }
            const fieldObjPrice = {
                ...initialAssessmentPriceField,
                isDynamic: true,
                id: priceFieldId,
                defaultValue: null,
            }
            const fieldObjType = {
                ...assessmentTypeField,
                isDynamic: true,
                id: typeFieldId,
                defaultValue: "",
            }
            dynamicAssessmentField.fields.push(fieldObj);
            dynamicAssessmentField.fields.push(fieldObjPrice);
            dynamicAssessmentField.fields.push(fieldObjType);
            
            form.setValue(durationFieldId, null);
            form.setValue(priceFieldId, null);
            const currentValues = form.getValues();
            const newSchema = generateValidationSchema(currentValues);
            setValidationSchema(newSchema); // This will re-trigger useForm with new resolver

            setAssessmentFields(prev => [...prev, dynamicAssessmentField]);
        }

        if (field.action === "addFollowUpTreatmentType") {
            let dynamicTreatmentField = {
                id:"addTreatment",
                fields: []
            };
            const dynamicFieldsCount = treatmentFields.length;
            const durationFieldId = `${followUpTreatmentDurationField.id}-${dynamicFieldsCount + 1}`;
            const priceFieldId = `${followUpTreatmentPriceField.id}-${dynamicFieldsCount + 1}`;
            const typeFieldId = `${followUpTreatmentTypeField.id}-${dynamicFieldsCount + 1}`;
            const fieldObj = {
                ...followUpTreatmentDurationField,
                isDynamic: true,
                id: durationFieldId,
                deleteId: durationFieldId,
                defaultValue: null,
            }
            const fieldObjPrice = {
                ...followUpTreatmentPriceField,
                isDynamic: true,
                id: priceFieldId,
                deleteId: priceFieldId,
                defaultValue: null,
            }
            const fieldObjType = {
                ...followUpTreatmentTypeField,
                isDynamic: true,
                id: typeFieldId,
                deleteId: typeFieldId,
                defaultValue: "",
            }
            dynamicTreatmentField.fields.push(fieldObj);
            dynamicTreatmentField.fields.push(fieldObjPrice);
            dynamicTreatmentField.fields.push(fieldObjType);

            form.setValue(durationFieldId, null);
            form.setValue(priceFieldId, null);
            const currentValues = form.getValues();
            const newSchema = generateValidationSchema(currentValues);
            setValidationSchema(newSchema); // This will re-trigger useForm with new resolver

            setTreatmentFields(prev => [...prev, dynamicTreatmentField]);
        }
    };

    const handleDeleteDynamicField = (id, type) => {
        if (type === "initial") {
            setAssessmentTypeFields(prev => prev.filter(f => f.id !== id));
        } else if (type === "followUp") {
            setFollowUpTreatmentTypes(prev => prev.filter(f => f.id !== id));
        }
    };

    const onSubmit = async (data) => {

        const assessmentType = [];
        const assessmentDuration = [];
        const assessmentPrice = [];
        const followUpTreatmentType = [];
        const followUpTreatmentDuration = [];
        const followUpTreatmentPrice = [];

        Object.keys(data).forEach((key) => {
            if (key.startsWith('assessmentType')) {
                assessmentType.push(data[key]);
            }
            if (key.startsWith('initialAssessmentDuration')) {
                assessmentDuration.push(data[key]);
            }
            if (key.startsWith('initialAssessmentPrice')) {
                assessmentPrice.push(data[key]);
            }
            if (key.startsWith('followUpTreatmentType')) {
                followUpTreatmentType.push(data[key]);
            }
            if (key.startsWith('followUpTreatmentDuration')) {
                followUpTreatmentDuration.push(data[key]);
            }
            if (key.startsWith('followUpTreatmentPrice')) {
                followUpTreatmentPrice.push(data[key]);
            }
        });

        const payload = {
            btn_submit: true,
            listings: [
                {
                    listing_id: details?.ID,
                    title: details?.post_title,
                    address_1: details?.data.address_1,
                    postcode: details?.data.postcode,
                    telephone_1: details?.data?.phone?.raw,
                    specialization: (Object.values(details.data.services || {})),
                    treatments: Object.values(details.data.treatments || {}),
                    provideruid: currentUserId,
                    uid: currentUserId,
                    // assessment_type: assessmentType,
                    // followup_treatment_type: followUpTreatmentType,
                    // initial_appointment_cost: data.initialAssessmentPrice,
                    // initial_appointment_time: data.initialAssessmentDuration,
                    // subsequent_appointment_cost: data.followUpTreatmentPrice,
                    // subsequent_appointment_time: data.followUpTreatmentDuration,
                    initial_assessment: assessmentDuration.map((duration, durationIndex) => {
                        let retObject = {
                        Initial_assessment_duration: duration,
                        Initial_assessment_cost: assessmentPrice?.[durationIndex] || "",
                        Initial_assessment_assessment_type: assessmentType?.[durationIndex] || "",
                        };
                        return retObject;
                    }),
                    follow_up_treatment: followUpTreatmentDuration.map((duration, durationIndex) => {
                        let retObject = {
                        follow_up_treatment_duration: duration,
                        follow_up_treatment_cost: followUpTreatmentPrice?.[durationIndex] || "",
                        follow_up_treatment_type: followUpTreatmentType?.[durationIndex] || "",
                        };
                        return retObject;
                    })
                }
            ]
        };
        try {
            const res = await axiosProxyInstance.post('/signup/2/', payload);
            cogoToast.success("Appointment Prices Updated Successfully!");
        } catch (err) {
            console.error("Submission failed:", err);
        }
    }

    return (
        <Box>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Box className='margin-top-15'>
                    <Box style={{ flex: "1" }}>
                        <Box>
                            <Typography variant="gothamMedium" className="f-500">
                                Initial assessment
                            </Typography>
                        </Box>
                        {
                            assessmentFields?.map((assessment, assessmentIndex) => (
                                <Box key={`${assessment}-${assessmentIndex+1}`}>
                                    <Box className={styles.formFields}>
                                        <Box style={{ flex: "1", marginTop: '20px' }}>
                                            <Typography variant="gothamRegular">
                                                Enter Duration
                                            </Typography>
                                            <FormField
                                                field={{ ...assessment?.fields[0], defaultValue: assessment?.fields[0].defaultValue }}
                                                register={register}
                                                control={control}
                                                errors={errors}
                                                watch={watch}
                                                form={form}
                                                setValue={setValue}
                                            />
                                        </Box>
                                        <Box style={{ flex: "1", marginTop: '20px' }}>
                                            <Typography variant="gothamRegular">
                                                Enter Price
                                            </Typography>
                                            <FormField
                                                field={{ ...assessment?.fields[1], defaultValue: assessment?.fields[1].defaultValue }}
                                                register={register}
                                                control={control}
                                                errors={errors}
                                                watch={watch}
                                                form={form}
                                                setValue={setValue}
                                            />
                                        </Box>
                                    </Box>
                                    <Box sx={{ flex: "1", marginTop: '20px' }}>
                                        <Typography variant="gothamRegular">
                                            Select assessment type (optional)
                                            <InfoOutlineIcon className={styles.infoIcon}/>
                                        </Typography>
                                        <FormField
                                            field={{ ...assessment?.fields[2], defaultValue: assessment?.fields[2].defaultValue }}
                                            register={register}
                                            control={control}
                                            errors={errors}
                                            watch={watch}
                                            form={form}
                                            setValue={setValue}
                                        />
                                    </Box>
                                </Box>
                            ))
                        }
                        {
                            assessmentFields?.length < 4 && (
                                <FormField
                                    field={addInitialAssessmentTypeLinkField}
                                    onLinkButtonClick={(clickedField, index) =>
                                        handleDetailsLinkButtonClick(clickedField, index)
                                    }
                                    register={register}
                                    control={control}
                                    errors={errors}
                                />
                            )
                        }
                        <Box>
                            <Typography variant="gothamMedium" className="f-500 margin-top-30">
                                Follow-up treatment
                            </Typography>
                        </Box>
                        {
                            treatmentFields?.map((treatment, treatmentIndex) => (
                                <Box key={`${treatment}-${treatmentIndex+1}`}>
                                    <Box className={styles.formFields}>
                                        <Box style={{ flex: "1", marginTop: '20px' }}>
                                            <Typography variant="gothamRegular">
                                                Enter Duration
                                            </Typography>
                                            <FormField
                                                field={{ ...treatment?.fields[0], defaultValue: treatment?.fields[0].defaultValue }}
                                                register={register}
                                                control={control}
                                                errors={errors}
                                            />
                                        </Box>
                                        <Box style={{ flex: "1", marginTop: '20px' }}>
                                            <Typography variant="gothamRegular">
                                                Enter Price
                                            </Typography>
                                            <FormField
                                                field={{ ...treatment?.fields[1], defaultValue: treatment?.fields[1].defaultValue }}
                                                register={register}
                                                control={control}
                                                errors={errors}
                                            />
                                        </Box>
                                    </Box>
                                    <Box sx={{ flex: "1", marginTop: '20px' }}>
                                        <Typography variant="gothamRegular">
                                            Select follow-up treatment type (optional)
                                            <InfoOutlineIcon className={styles.infoIcon}/>
                                        </Typography>
                                        <FormField
                                            field={{ ...treatment?.fields[2], defaultValue: treatment?.fields[2].defaultValue }}
                                            register={register}
                                            control={control}
                                            errors={errors}
                                        />
                                    </Box>
                                </Box>
                            ))
                        }
                        {
                            treatmentFields?.length < 4 && (
                                <FormField
                                    field={addFollowUpTreatmentTypeLinkField}
                                    onLinkButtonClick={(clickedField, index) =>
                                        handleDetailsLinkButtonClick(clickedField, index)
                                    }
                                    register={register}
                                    control={control}
                                    errors={errors}
                                />
                            )
                        }

                        <Box className={styles.submitButtonContainer}>
                            <Button
                                className={styles.primaryButton}
                                size="large"
                                type="submit"
                            >
                                Save Changes
                            </Button>
                            <Button
                                className={styles.secondaryButton}
                                size="large"
                            >
                                Cancel
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </form>
        </Box>
    )
}
export default Appointment